import { createSelector } from 'reselect';
import REWARDS from 'rewards';
import { selectUser } from 'redux/selectors/user';
import { EMPTY_OBJECT } from 'redux/selectors/empty';

const selectState = (state) => state.rewards || EMPTY_OBJECT;

export const selectUnclaimedRewardsByType = (state) => selectState(state).unclaimedRewardsByType;
export const selectClaimedRewardsById = (state) => selectState(state).claimedRewardsById;
export const selectViewRateById = (state) => selectState(state).viewRateById;
export const selectViewRateForChannelId = (state, channelId) => selectViewRateById(state)[channelId];
export const selectClaimedRewards = createSelector(selectClaimedRewardsById, (byId) => Object.values(byId) || []);
export const selectClaimedRewardsByTransactionId = createSelector(selectClaimedRewards, (rewards) =>
  rewards.reduce((mapParam: Record<string, any>, reward: any) => {
    const map = mapParam;
    map[reward.transaction_id] = reward;
    return map;
  }, {})
);
export const selectUnclaimedRewards = (state) => selectState(state).unclaimedRewards;
export const selectFetchingRewards = (state) => !!selectState(state).fetching;
export const selectUnclaimedRewardValue = createSelector(selectUnclaimedRewards, (rewards) =>
  rewards.reduce((sum, reward) => sum + reward.reward_amount, 0)
);
export const selectClaimsPendingByType = (state) => selectState(state).claimPendingByType;

const selectIsClaimRewardPending = (state: any, props: any) => selectClaimsPendingByType(state)[props.reward_type];

export const makeSelectIsRewardClaimPending = () =>
  createSelector(
    selectClaimsPendingByType,
    (state, props) => props.reward_type,
    (claimsPendingByType, rewardType) => claimsPendingByType[rewardType]
  );
export const selectClaimErrorsByType = (state) => selectState(state).claimErrorsByType;

const selectClaimRewardError = (state: any, props: any) => selectClaimErrorsByType(state)[props.reward_type];

export const makeSelectClaimRewardError = () =>
  createSelector(
    selectClaimErrorsByType,
    (state, props) => props.reward_type,
    (claimErrorsByType, rewardType) => claimErrorsByType[rewardType]
  );

const selectRewardByType = (state, rewardType) =>
  selectUnclaimedRewards(state).find((reward) => reward.reward_type === rewardType);

export const makeSelectRewardByType = () =>
  createSelector(
    selectUnclaimedRewards,
    (state, rewardType) => rewardType,
    (unclaimedRewards, rewardType) => unclaimedRewards.find((reward) => reward.reward_type === rewardType)
  );

const selectRewardByClaimCode = (state, claimCode) =>
  selectUnclaimedRewards(state).find((reward) => reward.claim_code === claimCode);

export const makeSelectRewardByClaimCode = () =>
  createSelector(
    selectUnclaimedRewards,
    (state, claimCode) => claimCode,
    (unclaimedRewards, claimCode) => unclaimedRewards.find((reward) => reward.claim_code === claimCode)
  );
export const makeSelectRewardAmountByType = () =>
  createSelector(selectRewardByType, (reward) => (reward ? reward.reward_amount : 0));
export const selectRewardContentClaimIds = createSelector(selectState, (state) => state.rewardedContentClaimIds);
export const selectReferralReward = createSelector(
  selectUnclaimedRewards,
  (unclaimedRewards) => unclaimedRewards.filter((reward) => reward.reward_type === REWARDS.TYPE_REFERRAL)[0]
);
export const selectHasUnclaimedRefereeReward = createSelector(selectUnclaimedRewards, (unclaimedRewards) =>
  unclaimedRewards.some((reward) => reward.reward_type === REWARDS.TYPE_REFEREE)
);
export const selectIsClaimingInitialRewards = createSelector(selectClaimsPendingByType, (claimsPendingByType) => {
  return !!(claimsPendingByType[REWARDS.TYPE_NEW_USER] || claimsPendingByType[REWARDS.TYPE_CONFIRM_EMAIL]);
});
export const selectHasClaimedInitialRewards = createSelector(selectClaimedRewardsById, (claimedRewardsById) => {
  const claims = Object.values(claimedRewardsById);
  const newUserClaimed = !!claims.find((claim: any) => claim && claim.reward_type === REWARDS.TYPE_NEW_USER);
  const confirmEmailClaimed = !!claims.find((claim: any) => claim && claim.reward_type === REWARDS.TYPE_CONFIRM_EMAIL);
  return newUserClaimed && confirmEmailClaimed;
});
export const selectWeeklyWatchClaimedThisWeek = createSelector(selectUnclaimedRewards, (unclaimedRewards) => {
  const weeklyWatch = unclaimedRewards.find((x) => x.reward_type === REWARDS.TYPE_WEEKLY_WATCH);

  if (weeklyWatch && weeklyWatch.data && weeklyWatch.data.last_claimed) {
    const last = new Date(weeklyWatch.data.last_claimed);
    const diff = new Date().getTime() - last.getTime();
    const diffDays = diff / (1000 * 60 * 60 * 24);
    return diffDays < 6.5;
  }

  return undefined;
});
export const selectIsRewardApproved = createSelector(selectUser, (user) => user && user.is_reward_approved);
